/* ============================================================
 * FREE GIFT AUTOMATION — RULE-DRIVEN CART RECONCILER
 * ============================================================
 * One reconciler, N rules. Each rule keeps EXACTLY ONE free
 * line in the cart while the cart qualifies, and zero lines
 * when it does not.
 *
 *   qualifies  = paidQty > 0 AND paidSubtotal >= rule.minSubtotal
 *   qualifies  -> exactly one gift line for that rule
 *   !qualifies -> zero gift lines for that rule
 *
 * Gift lines are identified by their hidden line property, never
 * by variant id, so a customer can buy the same product AND
 * receive it free as two separate lines.
 *
 * IMPORTANT: this theme cannot set a line's price. A genuinely
 * $0.00 gift line requires the rule to point at a $0.00 product.
 * Nothing here attempts to zero a price.
 *
 * Config: snippets/gift-rules.liquid (single source of truth)
 * ============================================================ */
(function () {
  'use strict';

  var CFG = window.GiftRules || {};
  var PREFIX = CFG.prefix || '_free_gift';
  var SOURCE = 'gift-reconciler';
  var MAX_PASSES = 8;
  var DEBOUNCE_MS = 150;
  var SWEEPS_MS = [1200, 3000];
  var OUR_HEADER = 'X-Gift-Reconciler';

  var RULES = (CFG.rules || []).filter(function (r) {
    return r && r.prop && r.variantId;
  });
  var ACTIVE_PROPS = {};
  RULES.forEach(function (r) {
    ACTIVE_PROPS[r.prop] = r;
  });
  var HIDDEN_HANDLES = CFG.hiddenHandles || [];

  var R = window.routes || {};
  var ADD_URL = R.cart_add_url || '/cart/add.js';
  var CHANGE_URL = R.cart_change_url || '/cart/change.js';
  var CART_URL = R.cart_url || '/cart';

  /* Captured at load so our own writes never re-enter our own hooks.
     Belt and braces: every request we make is also tagged with
     OUR_HEADER, which any wrapper (ours or a later re-chain) ignores. */
  var nativeFetch = window.fetch && window.fetch.bind(window);

  /* ==========================================================
   * MONEY
   * The theme has no JS money formatter — every amount is Liquid
   * `money`. We rebuild that filter from shop.money_format so
   * server-printed and JS-printed amounts agree by construction.
   * ========================================================== */
  var MONEY_FORMAT = CFG.moneyFormat || '${{amount}}';
  var MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  function withDelimiters(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents === null) cents = 0;
    var fixed = (cents / 100.0).toFixed(precision);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  function formatMoney(cents) {
    var match = MONEY_FORMAT.match(MONEY_PLACEHOLDER);
    if (!match) return MONEY_FORMAT;
    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', '.');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = withDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }
    return MONEY_FORMAT.replace(MONEY_PLACEHOLDER, value);
  }

  /* Re-render every server-printed amount through the same formatter,
     so a Liquid-rendered figure and a JS-updated one can never diverge.
     The Liquid output stays as the no-JS fallback. */
  function hydrateMoney(root) {
    var nodes = (root || document).querySelectorAll('[data-gift-money][data-gift-cents]');
    for (var i = 0; i < nodes.length; i++) {
      var cents = parseInt(nodes[i].getAttribute('data-gift-cents'), 10);
      if (!isNaN(cents)) nodes[i].innerHTML = formatMoney(cents);
    }
  }

  /* ==========================================================
   * CART READS / WRITES
   * ========================================================== */
  function ourFetch(url, options) {
    var opts = options || {};
    opts.credentials = 'same-origin';
    opts.headers = opts.headers || {};
    opts.headers[OUR_HEADER] = '1';
    var f = nativeFetch || window.fetch;
    return f(url, opts);
  }

  function getCart() {
    return ourFetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function addGift(rule) {
    var props = {};
    props[rule.prop] = rule.label || 'true';
    return ourFetch(ADD_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/javascript',
      },
      body: JSON.stringify({ id: rule.variantId, quantity: 1, properties: props }),
    }).then(function (r) {
      return r.json();
    });
  }

  function setLineQuantity(key, quantity) {
    return ourFetch(CHANGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    }).then(function (r) {
      return r.json();
    });
  }

  /* ==========================================================
   * STATE — the ONE place paidSubtotal is computed.
   * Mirrors the Liquid preamble in snippets/gift-rules.liquid.
   * ========================================================== */
  function isGiftPropKey(key) {
    return key === PREFIX || key.indexOf(PREFIX + '_') === 0;
  }

  function lineGiftProp(item) {
    var props = item && item.properties;
    if (!props) return null;
    for (var key in props) {
      if (!Object.prototype.hasOwnProperty.call(props, key)) continue;
      if (isGiftPropKey(key) && props[key] !== null && props[key] !== '') return key;
    }
    return null;
  }

  /* A pre-existing gift line added before this script shipped carries no
     property. Only ever treat a ZERO-PRICED line of a gift handle that
     way — a priced line of the same handle is a real purchase. */
  function legacyRuleFor(item) {
    if (!item || item.price !== 0) return null;
    if (HIDDEN_HANDLES.indexOf(item.handle) === -1) return null;
    for (var i = 0; i < RULES.length; i++) {
      if (RULES[i].handle === item.handle) return RULES[i];
    }
    return null;
  }

  function computePaidState(cart) {
    var items = (cart && cart.items) || [];
    var state = { paidQty: 0, paidSubtotal: 0, byRule: {}, orphans: [], cart: cart };

    items.forEach(function (item) {
      var prop = lineGiftProp(item);

      if (prop) {
        if (ACTIVE_PROPS[prop]) {
          (state.byRule[prop] = state.byRule[prop] || []).push(item);
        } else {
          // Gift-prefixed property matching no active rule — merchant
          // disabled the promo while this cart was live.
          state.orphans.push(item);
        }
        return;
      }

      var legacy = legacyRuleFor(item);
      if (legacy) {
        (state.byRule[legacy.prop] = state.byRule[legacy.prop] || []).push(item);
        return;
      }

      state.paidQty += item.quantity;
      state.paidSubtotal += item.final_line_price;
    });

    return state;
  }

  function qualifies(rule, state) {
    if (rule.available === false) return false;
    return state.paidQty > 0 && state.paidSubtotal >= rule.minSubtotal;
  }

  /* Exactly ONE correction per pass. Applied, then the cart is re-read. */
  function nextCorrection(state) {
    if (state.orphans.length) {
      return { type: 'remove', key: state.orphans[0].key, reason: 'orphan' };
    }

    for (var i = 0; i < RULES.length; i++) {
      var rule = RULES[i];
      var lines = state.byRule[rule.prop] || [];

      if (!qualifies(rule, state)) {
        if (lines.length) return { type: 'remove', key: lines[0].key, reason: 'not-qualified' };
        continue;
      }

      // Self-healing: a line whose variant no longer matches the rule
      // is removed; the next pass adds the correct one.
      for (var j = 0; j < lines.length; j++) {
        if (lines[j].variant_id !== rule.variantId) {
          return { type: 'remove', key: lines[j].key, reason: 'stale-variant' };
        }
      }

      if (lines.length === 0) return { type: 'add', rule: rule, reason: 'missing' };
      if (lines.length > 1) return { type: 'remove', key: lines[1].key, reason: 'duplicate' };
      if (lines[0].quantity !== 1) {
        return { type: 'quantity', key: lines[0].key, quantity: 1, reason: 'clamp' };
      }
    }

    return null;
  }

  /* ==========================================================
   * RECONCILE — re-entrancy latched, never concurrent.
   * ========================================================== */
  var running = false;
  var queued = false;
  var tail = Promise.resolve();
  var lastState = null;
  var addedRules = [];

  function runOnce() {
    addedRules = [];
    var changed = false;

    function pass(n) {
      return getCart().then(function (cart) {
        var state = computePaidState(cart);
        lastState = state;

        if (n >= MAX_PASSES) return state;

        var correction = RULES.length ? nextCorrection(state) : null;
        if (!correction) return state;

        var applied;
        if (correction.type === 'add') {
          addedRules.push(correction.rule);
          applied = addGift(correction.rule);
        } else if (correction.type === 'quantity') {
          applied = setLineQuantity(correction.key, correction.quantity);
        } else {
          applied = setLineQuantity(correction.key, 0);
        }

        changed = true;
        return applied.then(function () {
          return pass(n + 1);
        });
      });
    }

    return pass(0).then(function (state) {
      return (changed ? repaint(state) : Promise.resolve(state)).then(function (finalState) {
        broadcast(finalState || state, changed);
        if (changed && addedRules.length) showToasts(addedRules);
        return finalState || state;
      });
    });
  }

  function kick() {
    if (running) {
      queued = true;
      return tail;
    }
    running = true;
    var p = runOnce()
      .catch(function (e) {
        // Fail silently — the cart is re-read on the next signal.
        if (window.console && CFG.designMode) console.warn('[gift]', e);
      })
      .then(function () {
        running = false;
        if (queued) {
          queued = false;
          return kick();
        }
      });
    tail = p;
    return p;
  }

  /* ==========================================================
   * REPAINT
   * We mutate the cart behind the theme's back, so nothing
   * repaints on its own. Ask Shopify to re-render the theme's
   * own sections and swap them in — correct for BOTH additions
   * and removals, and no page reload.
   * ========================================================== */
  function sectionTargets() {
    var targets = [{ id: 'cart-icon-bubble', section: 'cart-icon-bubble' }];
    var items = document.getElementById('main-cart-items');
    var footer = document.getElementById('main-cart-footer');
    if (items && items.dataset.id) targets.push({ id: 'main-cart-items', section: items.dataset.id });
    if (footer && footer.dataset.id) targets.push({ id: 'main-cart-footer', section: footer.dataset.id });
    if (items || footer) targets.push({ id: 'cart-live-region-text', section: 'cart-live-region-text' });
    return targets;
  }

  function parse(html) {
    try {
      return new DOMParser().parseFromString(html, 'text/html');
    } catch (e) {
      return null;
    }
  }

  function repaint(state) {
    var targets = sectionTargets();
    var ids = targets
      .map(function (t) {
        return t.section;
      })
      .join(',');
    var path = window.location.pathname;
    var url = path + (window.location.search ? window.location.search + '&' : '?') + 'sections=' + encodeURIComponent(ids);

    return ourFetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (sections) {
        applySections(sections, targets, state);
        return state;
      })
      .catch(function () {
        // Section rendering unavailable — fall back to direct DOM
        // reconciliation, where the two directions are NOT symmetrical.
        fallbackReconcileDom(state);
        return state;
      });
  }

  function applySections(sections, targets, state) {
    targets.forEach(function (t) {
      var html = sections && sections[t.section];
      if (!html) return;
      var doc = parse(html);
      if (!doc) return;

      if (t.id === 'cart-icon-bubble') {
        // Surgical: swap only the count bubble so the header keeps its
        // own icon and aria-label instead of the standalone section's.
        var host = document.getElementById('cart-icon-bubble');
        if (!host) return;
        var fresh = doc.querySelector('.cart-count-bubble');
        var current = host.querySelector('.cart-count-bubble');
        if (fresh && current) current.replaceWith(fresh);
        else if (fresh) host.appendChild(fresh);
        else if (current) current.remove();
        return;
      }

      var host2 = document.getElementById(t.id);
      if (!host2) return;
      var target = host2.querySelector('.js-contents');
      var source = doc.querySelector('.js-contents');
      if (target && source) target.innerHTML = source.innerHTML;
      else if (t.id === 'cart-live-region-text') {
        var live = doc.querySelector('.shopify-section');
        if (live) host2.innerHTML = live.innerHTML;
      }
    });

    var isEmpty = state && state.cart && state.cart.item_count === 0;
    var cartItemsEl = document.querySelector('cart-items');
    var footerEl = document.getElementById('main-cart-footer');
    var itemsEl = document.getElementById('main-cart-items');
    if (cartItemsEl) cartItemsEl.classList.toggle('is-empty', !!isEmpty);
    if (footerEl) footerEl.classList.toggle('is-empty', !!isEmpty);
    if (itemsEl) itemsEl.classList.toggle('is-empty', !!isEmpty);

    hydrateMoney(document);
  }

  /* Fallback only. Never reloads unconditionally:
   *   removed line -> drop its row, no reload
   *   added line   -> only Liquid can draw a new row -> reload
   *   cart empty   -> only Liquid can draw the empty state -> reload
   *   otherwise    -> nothing */
  function fallbackReconcileDom(state) {
    var rows = document.querySelectorAll('.cart-item');
    if (!rows.length) return;

    var cart = state && state.cart;
    if (!cart) return;

    if (cart.item_count === 0) {
      window.location.reload();
      return;
    }

    var liveKeys = {};
    (cart.items || []).forEach(function (i) {
      liveKeys[i.key] = true;
    });

    var domKeys = {};
    var removedAny = false;
    for (var i = 0; i < rows.length; i++) {
      var key = rows[i].getAttribute('data-gift-key') || rows[i].dataset.key || '';
      if (!key) continue;
      domKeys[key] = true;
      if (!liveKeys[key]) {
        rows[i].remove();
        removedAny = true;
      }
    }

    var added = (cart.items || []).some(function (i) {
      return !domKeys[i.key];
    });
    if (added) window.location.reload();
    else if (!removedAny) {
      /* nothing to do */
    }
  }

  /* ==========================================================
   * BROADCAST — every pass, including passes that changed nothing.
   * A plain quantity edit moves the meter without touching a gift
   * line, so "only broadcast on change" would leave the UI stale.
   * ========================================================== */
  function broadcast(state, changed) {
    if (!state) return;

    updateMeters(state);

    var detail = {
      source: SOURCE,
      cart: state.cart,
      paidSubtotal: state.paidSubtotal,
      paidQty: state.paidQty,
      changed: !!changed,
      rules: RULES,
    };

    try {
      document.dispatchEvent(new CustomEvent('gift:reconciled', { detail: detail, bubbles: true }));
    } catch (e) {
      /* older browsers — nothing depends on this */
    }

    // Let the theme's own components repaint, tagged so our listener
    // ignores it and we do not loop.
    if (changed && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: state.cart });
      } catch (e) {
        /* ignore */
      }
    }
  }

  /* ==========================================================
   * PROGRESS METER — reports, never decides.
   * Reads paidSubtotal straight off the reconciler's state.
   * ========================================================== */
  var meterSeen = {};

  function updateMeters(state) {
    var meters = document.querySelectorAll('[data-gift-meter]');
    if (!meters.length) return;

    for (var i = 0; i < meters.length; i++) {
      var el = meters[i];
      var prop = el.getAttribute('data-gift-rule');
      var min = parseInt(el.getAttribute('data-gift-min'), 10) || 0;
      var title = el.getAttribute('data-gift-title') || '';
      var paid = state.paidSubtotal;
      var unlocked = state.paidQty > 0 && paid >= min;
      var remaining = Math.max(0, min - paid);

      // Floor, and hold at 99 until genuinely unlocked — a full track
      // beside "$1.00 away" reads as a bug.
      var pct = unlocked ? 100 : min > 0 ? Math.min(99, Math.floor((paid * 100) / min)) : 0;
      if (pct < 0) pct = 0;

      var fill = el.querySelector('[data-gift-meter-fill]');
      if (fill) fill.style.width = pct + '%';

      var track = el.querySelector('.giftmeter__track');
      if (track) track.setAttribute('aria-valuenow', String(pct));

      var badge = el.querySelector('.giftmeter__badge-num');
      if (badge) {
        badge.setAttribute('data-gift-cents', String(remaining));
        badge.innerHTML = formatMoney(remaining);
      }

      var eyebrow = el.querySelector('[data-gift-meter-eyebrow]');
      if (eyebrow) eyebrow.textContent = unlocked ? 'Unlocked' : 'Free gift';

      var msg = el.querySelector('[data-gift-meter-message]');
      if (msg) {
        msg.innerHTML = unlocked
          ? 'Unlocked. A free <b class="giftmeter__em">' + escapeHtml(title) + '</b> is in your cart.'
          : '<b class="giftmeter__em" data-gift-money data-gift-cents="' +
            remaining +
            '">' +
            formatMoney(remaining) +
            '</b> away from a free <b class="giftmeter__em">' +
            escapeHtml(title) +
            '</b>.';
      }

      el.classList.toggle('giftmeter--unlocked', unlocked);

      // Celebrate the CROSSING only — never a page that merely loads
      // already unlocked.
      var was = meterSeen[prop];
      if (was === false && unlocked) celebrate(el);
      meterSeen[prop] = unlocked;
    }
  }

  function celebrate(el) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    el.classList.remove('giftmeter--celebrate');
    void el.offsetWidth;
    el.classList.add('giftmeter--celebrate');
    setTimeout(function () {
      el.classList.remove('giftmeter--celebrate');
    }, 1400);
  }

  /* ==========================================================
   * TOAST + BUBBLE
   * ========================================================== */
  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function bumpBubble() {
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    bubble.classList.remove('fg-bump');
    void bubble.offsetWidth;
    bubble.classList.add('fg-bump');
  }

  var TOAST_STAGGER_MS = 450;
  var MAX_TOASTS = 3;
  var toastStack = null;

  function ensureToastStack() {
    if (toastStack && toastStack.isConnected) return toastStack;
    toastStack = document.querySelector('.fg-toast-stack');
    if (!toastStack) {
      toastStack = document.createElement('div');
      toastStack.className = 'fg-toast-stack';
      document.body.appendChild(toastStack);
    }
    return toastStack;
  }

  /* Two gifts can land in the same reconcile pass (an order that clears the
     threshold from an empty cart adds both). Announce each one, in rule
     order, staggered so the second slides in after the first has settled. */
  function showToasts(rules) {
    rules.forEach(function (rule, i) {
      setTimeout(function () {
        showToast(rule);
      }, i * TOAST_STAGGER_MS);
    });
  }

  function toastFor(prop) {
    var stack = ensureToastStack();
    for (var i = 0; i < stack.children.length; i++) {
      if (stack.children[i].getAttribute('data-gift-toast') === prop) return stack.children[i];
    }
    return null;
  }

  function showToast(rule) {
    if (!CFG.toast || CFG.toast.show === false) return;
    if (window.Shopify && window.Shopify.designMode) return;

    // Copy is derived from the rule, never hand-typed per surface.
    var title = rule.minSubtotal > 0
      ? 'Free ' + (rule.displayTitle || rule.title) + ' unlocked'
      : CFG.toast.message || 'Free gift added';
    var sub = CFG.toast.submessage || '';

    var stack = ensureToastStack();
    // One live toast per rule — a re-announcement replaces its own, and
    // never stacks a duplicate next to it.
    var existing = toastFor(rule.prop);
    if (existing) existing.remove();

    var media = rule.image
      ? '<span class="fg-toast__media"><img src="' + escapeHtml(rule.image) + '" alt="" loading="lazy"></span>'
      : '<span class="fg-toast__icon" aria-hidden="true">&#127873;</span>';

    var toast = document.createElement('div');
    toast.className = 'fg-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML =
      media +
      '<span class="fg-toast__text">' +
      '<span class="fg-toast__title">' +
      escapeHtml(title) +
      '</span>' +
      (sub ? '<span class="fg-toast__sub">' + escapeHtml(sub) + '</span>' : '') +
      '</span>' +
      '<button type="button" class="fg-toast__close" aria-label="Dismiss">&times;</button>' +
      '<span class="fg-toast__bar"></span>';

    toast.setAttribute('data-gift-toast', rule.prop);
    stack.appendChild(toast);

    // Oldest first in DOM order; drop the oldest once the stack is full.
    while (stack.children.length > MAX_TOASTS) stack.removeChild(stack.firstChild);

    requestAnimationFrame(function () {
      toast.classList.add('fg-toast--in');
    });

    var timer = setTimeout(dismiss, 4200);
    var dismissed = false;
    function dismiss() {
      if (dismissed) return;
      dismissed = true;
      clearTimeout(timer);
      // Collapse this toast's own slot as it leaves so anything stacked
      // above it slides down instead of snapping.
      var height = toast.offsetHeight;
      toast.classList.remove('fg-toast--in');
      toast.classList.add('fg-toast--out');
      requestAnimationFrame(function () {
        toast.style.marginBottom = -(height + 10) + 'px';
      });
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 350);
    }
    toast.querySelector('.fg-toast__close').addEventListener('click', dismiss);
  }

  /* ==========================================================
   * CHANGE SIGNALS
   * window.fetch is contested territory: app scripts patch it too,
   * and whichever assigns LAST wins. So we chain onto whatever is
   * installed at that moment, re-check that we are still installed,
   * and back it with XHR, form-submit, pubsub, focus and bfcache.
   * ========================================================== */
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;
  var debounceTimer = null;
  var sweepTimers = [];

  function signal() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      installFetchHook();
      kick();
    }, DEBOUNCE_MS);

    // Verification sweeps. Reconcile is idempotent and costs one GET,
    // so a missed signal self-corrects instead of stranding the customer.
    sweepTimers.forEach(clearTimeout);
    sweepTimers = SWEEPS_MS.map(function (ms) {
      return setTimeout(kick, ms);
    });
  }

  var installedFetch = null;

  function requestIsOurs(input, init) {
    try {
      if (init && init.headers) {
        if (typeof init.headers.get === 'function') return !!init.headers.get(OUR_HEADER);
        if (init.headers[OUR_HEADER]) return true;
      }
      if (input && typeof input !== 'string' && input.headers && typeof input.headers.get === 'function') {
        return !!input.headers.get(OUR_HEADER);
      }
    } catch (e) {
      /* ignore */
    }
    return false;
  }

  function installFetchHook() {
    var current = window.fetch;
    if (!current || current === installedFetch) return;

    var chained = function (input, init) {
      var url = '';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
      } catch (e) {
        /* ignore */
      }
      var mine = requestIsOurs(input, init);
      var promise = current.apply(this, arguments);
      if (!mine && CART_MUTATION.test(url)) {
        promise.then(
          function (res) {
            if (res && res.ok) signal();
          },
          function () {}
        );
      }
      return promise;
    };

    installedFetch = chained;
    try {
      window.fetch = chained;
    } catch (e) {
      /* frozen fetch — the other signals still cover us */
    }
  }

  function installXhrHook() {
    var proto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (!proto || proto.__giftHooked) return;
    proto.__giftHooked = true;

    var open = proto.open;
    var send = proto.send;

    proto.open = function (method, url) {
      try {
        this.__giftUrl = url || '';
      } catch (e) {
        /* ignore */
      }
      return open.apply(this, arguments);
    };

    proto.send = function () {
      try {
        if (CART_MUTATION.test(this.__giftUrl || '')) {
          this.addEventListener('load', function () {
            if (this.status >= 200 && this.status < 300) signal();
          });
        }
      } catch (e) {
        /* ignore */
      }
      return send.apply(this, arguments);
    };
  }

  function installFormHook() {
    // Capture phase: fires even if a handler later stops propagation,
    // and regardless of the transport the theme or an app uses.
    document.addEventListener(
      'submit',
      function (e) {
        var form = e.target;
        if (!form || form.tagName !== 'FORM') return;
        var action = form.getAttribute('action') || form.action || '';
        if (CART_MUTATION.test(action)) signal();
      },
      true
    );
  }

  /* ==========================================================
   * BOOT
   * ========================================================== */
  function init() {
    hydrateMoney(document);

    // Seed the meter's locked/unlocked memory from the server-rendered
    // state so an already-unlocked page load does not celebrate.
    var meters = document.querySelectorAll('[data-gift-meter]');
    for (var i = 0; i < meters.length; i++) {
      meterSeen[meters[i].getAttribute('data-gift-rule')] = meters[i].classList.contains('giftmeter--unlocked');
    }

    if (!RULES.length) return;

    installFetchHook();
    installXhrHook();
    installFormHook();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
        if (data && data.source === SOURCE) return; // never loop on ourselves
        bumpBubble();
        signal();
      });
    }

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) {
        installFetchHook();
        kick();
      }
    });

    window.addEventListener('pageshow', function (e) {
      if (e && e.persisted) kick(); // bfcache restore
    });

    // A late-loading app script can reassign window.fetch after us.
    var recheck = 0;
    var recheckTimer = setInterval(function () {
      installFetchHook();
      if (++recheck >= 30) clearInterval(recheckTimer);
    }, 1000);

    kick(); // every page load heals the cart
  }

  window.GiftReconciler = {
    reconcile: kick,
    settle: kick, // idempotent: one GET when nothing needs correcting
    state: function () {
      return lastState;
    },
    paidSubtotal: function () {
      return lastState ? lastState.paidSubtotal : null;
    },
    formatMoney: formatMoney,
    showToasts: showToasts, // debug handle: GiftReconciler.showToasts(GiftRules.rules)
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
